import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgressBarContext, UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import { BrandContext, TEMPO } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import useAmexBCE from 'hooks/useAmexBCE';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import useQuery from 'hooks/useQuery';
import useUsaa from 'hooks/useUsaa';
import useWelcomeQuery from 'hooks/useWelcomeQuery';
import {
  STANDARD,
  FAMILY,
  SHOW_PROMO_BANNER_LOCAL_STORAGE,
  PARTNERSHIP_RESOURCE_TYPES,
  HEROES_REFERRER,
} from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import { createEvents, getFunnelMenu } from 'services/RequestService/endpoints/signUp';

import HomeChefHeader from './HomeChefHeader';
import IntroContainerSkeleton from './IntroContainerSkeleton';
import IntroInformationForm from './IntroInformationForm';
import IntroMenuSlideout from './IntroMenuSlideout';
import Questions from './Questions';
import SignInButtonSection from './SignInButtonSection';
import TempoHeader from './TempoHeader';
import UsaaLegal from './UsaaLegal';

const IntroContainer = () => {
  const isAmexUser = useAmexBCE();
  const { brand } = useContext(BrandContext);
  const { setUsaaMemberId } = useContext(VoucherContext);
  const { setProgress } = useContext(ProgressBarContext);
  const { user } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);
  const isTempo = useBrand(TEMPO);
  const isUsaa = useUsaa();
  const [partnerReferrer, setPartnerReferrer] = useState(null);
  const [mealPlanPreference, setMealPlanPreference] = useState(STANDARD);
  const [menuMeals, setMenuMeals] = useState({});
  const [welcomeData, setWelcomeData] = useState({
    visibleMealKitTraits: [],
    userSignedIn: false,
  });
  const [showMenuSlideout, setShowMenuSlideout] = useState(false);
  const { isQueryParamTrue: hasKrogerParam } = useQuery('kroger', 'true');
  const { isQueryParamTrue: hasMenuParam } = useQuery('menu', 'true');
  const { queryParams } = useQuery();
  const { isPending: isLoadingPartnerData, data: partnershipData } =
    usePartnershipQuery(partnerReferrer);
  const { mealPlanSlug } = useParams();
  const { visibleMealKitTraits } = welcomeData;
  const weeklyMeals = queryParams.get('meals');
  const servings = queryParams.get('servings');
  const isFamilyLead = mealPlanPreference === FAMILY;
  const partnershipLogo = partnershipData?.partnership?.resources?.find(
    resource => resource.resourceType === PARTNERSHIP_RESOURCE_TYPES.LOGO
  )?.url;
  const showMenuReveal = hasMenuParam || localStorage.read('menu_reveal');

  const { isQueryParamTrue: isFromHeroesPage } = useQuery('heroes', 'true');
  isFromHeroesPage && localStorage.write(HEROES_REFERRER, true);

  useEffect(() => {
    if (hasMenuParam || showMenuReveal) {
      localStorage.write('menu_reveal', true);
      const fetchMenu = async () => {
        const funnelMenu = await getFunnelMenu({
          params: { mealPlan: mealPlanSlug, menu: hasMenuParam },
        });
        setMenuMeals(funnelMenu.meals);
      };
      fetchMenu();
    }
  }, [hasMenuParam, showMenuReveal, mealPlanSlug]);

  useEffect(() => {
    if (mealPlanSlug === TEMPO || isTempo) {
      setMealPlanPreference(TEMPO);
    } else if (mealPlanSlug === STANDARD) {
      setMealPlanPreference(STANDARD);
    } else if (mealPlanSlug === FAMILY) {
      setMealPlanPreference(FAMILY);
    } else {
      setMealPlanPreference(STANDARD);
    }
  }, [mealPlanSlug, setMealPlanPreference, isTempo]);

  const selectedTraitsFor = useCallback(
    initialTraits =>
      initialTraits.map(trait => ({
        ...trait,
        selected:
          user?.mealKitTraits?.some(selectedTrait => selectedTrait.name === trait.friendlyName) ||
          false,
      })),
    [user]
  );

  const onSignInClick = useAnalyticsCallback(
    null,
    ({ events, logEvent }) => {
      if (brand) {
        logEvent(events.signUp[brand].intro.signInClicked);
      }
    },
    [brand]
  );

  const {
    isPending: isLoadingWelcomeData,
    isError: isWelcomeError,
    error: welcomeError,
    data: response,
  } = useWelcomeQuery(mealPlanSlug);

  useAnalyticsEffect(
    ({ events, logEvent }) => {
      if (brand && mealPlanSlug && isLoadingWelcomeData === false) {
        logEvent(events.signUp[brand].intro.viewed, {
          mealPlanSlug,
        });
      }
    },
    [brand, mealPlanSlug, isLoadingWelcomeData]
  );

  useEffect(() => {
    if (response?.user) {
      if (response?.user.partnerReferrer) {
        setPartnerReferrer(response.user.partnerReferrer);
      }

      setWelcomeData(prevWelcomeData => ({
        ...prevWelcomeData,
        visibleMealKitTraits: selectedTraitsFor(response.user.mealKitTraits?.visibleMealKitTraits),
        userSignedIn: response.user.signedIn,
      }));
      if (response?.user.usaaMemberId) {
        setUsaaMemberId(response.user.usaaMemberId);
      }
    } else if (isWelcomeError) {
      return ErrorLogging.captureException(welcomeError);
    }
  }, [response, selectedTraitsFor, mealPlanSlug]);

  useEffect(() => {
    setProgress({
      percent: '9',
      stepNumber: '1',
      totalSteps: '5',
      nextStep: 'Preferences',
    });
    createEvents({ params: { event_name: 'PageView' } });
  }, []);

  useEffect(() => {
    if (voucher?.showPromoBanner) {
      localStorage.write(SHOW_PROMO_BANNER_LOCAL_STORAGE, true);
    }
  }, [voucher]);

  const handleMealKitTraitClick = useAnalyticsCallback(
    ({ friendlyName, selected }) => {
      const planTraits = ['Just me', 'Two of us', 'A group or family'];
      const isPlanTrait = planTraits.includes(friendlyName);

      const newVisibleMealKitState = visibleMealKitTraits.map(trait => {
        if (isPlanTrait) {
          if (trait.friendlyName === friendlyName) {
            return { ...trait, selected: !selected };
          } else if (planTraits.includes(trait.friendlyName)) {
            return { ...trait, selected: false };
          }
        } else {
          if (trait.friendlyName === friendlyName) {
            return { ...trait, selected: !selected };
          }
        }
        return trait;
      });

      if (newVisibleMealKitState)
        setWelcomeData(prevWelcomeData => ({
          ...prevWelcomeData,
          visibleMealKitTraits: newVisibleMealKitState,
        }));
    },
    ({ logEvent, events, callbackArgs }) => {
      if (!callbackArgs[0].selected) {
        if (brand) {
          logEvent(events.signUp[brand].intro.mealKitTraitClicked, {
            mealKitTraitName: callbackArgs[0].name,
          });
        }
      }
    },
    [visibleMealKitTraits, brand]
  );

  const waitingForData = useMemo(
    () => isLoadingWelcomeData || (!!partnerReferrer && isLoadingPartnerData),
    [isLoadingWelcomeData, isLoadingPartnerData, partnerReferrer]
  );

  const showPartnershipOrKrogerSection = (partnershipData?.ok && partnershipLogo) || hasKrogerParam;
  const showPartnershipLogo = partnershipData && partnershipLogo && !hasKrogerParam;

  return waitingForData ? (
    <IntroContainerSkeleton />
  ) : (
    <>
      {showPartnershipOrKrogerSection && (
        <div className="mx-auto max-w-xl text-center">
          {showPartnershipLogo && (
            <img
              style={{ maxWidth: '350px' }}
              className="pb-6 pt-8 bp1:pb-0"
              src={partnershipLogo}
              alt={`${partnershipData?.partnership.name} logo`}
            />
          )}
        </div>
      )}
      {isTempo ? (
        <TempoHeader />
      ) : (
        (!partnershipData || !partnershipData.ok) && <HomeChefHeader isFamilyLead={isFamilyLead} />
      )}

      <div className="mx-auto max-w-xl px-6 bp2:p-0">
        {visibleMealKitTraits.length > 0 && (
          <Questions
            visibleMealKitTraits={visibleMealKitTraits}
            isFamilyLead={isFamilyLead}
            handleMealKitTraitClick={handleMealKitTraitClick}
          />
        )}

        {showMenuReveal && (
          <>
            <button
              type="button"
              className={`textMd mt-4 w-full border-none p-0 text-center underline ${isTempo ? 'text-tempo-navy-100' : 'text-green-100'}`}
              onClick={() => setShowMenuSlideout(true)}
            >
              Take a look at the upcoming menu!
            </button>
            <IntroMenuSlideout
              showMenuSlideout={showMenuSlideout}
              setShowMenuSlideout={setShowMenuSlideout}
              menuMeals={menuMeals}
              visibleMealKitTraits={visibleMealKitTraits}
            />
          </>
        )}

        <IntroInformationForm
          mealPlanPreference={mealPlanPreference}
          selectedMealKitTraits={visibleMealKitTraits
            .filter(trait => trait.selected)
            .map(trait => trait.friendlyName)}
          isAmexUser={isAmexUser}
          brand={brand}
          weeklyMeals={weeklyMeals}
          servings={servings}
          hidePlanChangePage={partnershipData?.partnership?.hidePlanChangePage}
        />
        <SignInButtonSection
          onClick={onSignInClick}
          isUcl={Boolean(user?.id)}
          brand={brand}
          showConvenienceClaimSource={
            !isTempo && !hasKrogerParam && (!partnershipData || !partnershipData.ok)
          }
          containerClassName="text-center"
          textClassName="text-base pt-4"
        />
        {isUsaa && !isTempo && <UsaaLegal />}
      </div>
    </>
  );
};

export default IntroContainer;
